import { Injectable } from "@angular/core";
import { Subject, Observable } from "rxjs";
import { concatMap, takeUntil, delay } from "rxjs/operators";
import { DatadogService } from "../../../core/observability/datadog.service";

/**
 * 🎯 ALERT QUEUE SERVICE
 *
 * Serviço centralizado que gerencia a fila de alertas usando RxJS.
 * Garante que apenas 1 alerta seja processado por vez, evitando sobreposição.
 *
 * Princípios:
 * - Usa concatMap para processar eventos sequencialmente
 * - Cada componente se inscreve e controla quando terminou
 * - Fila única para donations, sales e qualquer outro tipo
 */

export interface AlertEvent {
  id: string;
  type: "donation" | "sale" | "goal";
  data: any;
  timestamp: number;
  generation: number;
  sourceId?: string;
  priority?: number; // High priority (>= 999) bypasses pause
}

export interface AlertPlaybackControl {
  complete: () => void; // Chama quando o alerta terminar completamente
  skip: () => void; // Chama para pular o alerta atual
}

@Injectable({
  providedIn: "root",
})
export class AlertQueueService {
  // 📥 Input: Eventos entram aqui
  private readonly eventQueue$ = new Subject<AlertEvent>();
  private pending: AlertEvent[] = [];
  private pausedBuffer: AlertEvent[] = [];
  private paused = false;
  private generation = 0; // usado para invalidar itens após clear
  private readonly interAlertDelayMs = 2000;
  private readonly skipCooldownMs = 300;
  private skipCooldownTimer?: any;

  // 🎛️ Controle: Sinaliza para cancelar o alerta atual
  private stopCurrentCtrl$ = new Subject<void>();
  public readonly stopSignal$ = this.stopCurrentCtrl$.asObservable();

  // �📤 Output: Componentes escutam aqui para receber alertas
  private readonly playbackSubject$ = new Subject<{
    event: AlertEvent;
    control: AlertPlaybackControl;
  }>();

  public readonly alertPlayback$ = this.playbackSubject$.asObservable();

  constructor(private datadogService: DatadogService) {
    this.initializeQueue();
  }

  /**
   * 🚀 Inicializa o pipeline de processamento
   */
  private initializeQueue(): void {
    this.eventQueue$
      .pipe(
        // 🔑 concatMap: Aguarda o anterior terminar antes de processar o próximo
        concatMap((event) =>
          this.processEvent(event).pipe(delay(this.interAlertDelayMs))
        )
      )
      .subscribe();
  }

  /**
   * 📥 Adiciona um evento à fila
   */
  public enqueue(
    event: Partial<AlertEvent> & { type: AlertEvent["type"]; data: any }
  ): void {
    const fullEvent: AlertEvent = {
      ...event,
      id: event.id || this.generateId(),
      timestamp: Date.now(),
      generation: this.generation,
      priority: event.priority || 0,
      // guard extra payloads if needed
    };

    this.pending.push(fullEvent);
    
    this.datadogService.logInfo(`[AlertQueue] Alert enqueued: ${fullEvent.id}`, {
      alertId: fullEvent.id,
      alertType: fullEvent.type,
      priority: fullEvent.priority,
      generation: fullEvent.generation,
      pendingCount: this.pending.length,
      isPaused: this.paused,
    });
    
    // High priority alerts (>= 999) bypass pause
    const isHighPriority = (fullEvent.priority || 0) >= 999;
    
    if (this.paused && !isHighPriority) {
      this.pausedBuffer.push(fullEvent);
      this.datadogService.logInfo(`[AlertQueue] Alert buffered due to pause: ${fullEvent.id}`, {
        alertId: fullEvent.id,
        alertType: fullEvent.type,
        bufferedCount: this.pausedBuffer.length,
      });
    } else {
      if (isHighPriority && this.paused) {
        this.datadogService.logInfo(`[AlertQueue] High priority alert bypassing pause: ${fullEvent.id}`, {
          alertId: fullEvent.id,
          alertType: fullEvent.type,
          priority: fullEvent.priority,
        });
      }
      this.eventQueue$.next(fullEvent);
    }
  }

  /**
   * 🎬 Processa um evento individual
   */
  private processEvent(event: AlertEvent): Observable<void> {
    return new Observable<void>((observer) => {
      // Se a fila foi limpa/pausada depois que este evento entrou, descarta e re-enfileira se estiver pausado
      if (event.generation !== this.generation) {
        this.removePending(event.id);
        if (this.paused) {
          const requeued: AlertEvent = {
            ...event,
            generation: this.generation,
          };
          this.pausedBuffer.push(requeued);
          this.pending.push(requeued);
        }
        observer.next();
        observer.complete();
        return;
      }
      // 🎮 Cria controles para o componente
      const control: AlertPlaybackControl = {
        complete: () => {
          this.datadogService.logInfo(`[AlertQueue] Alert completed: ${event.id}`, {
            alertId: event.id,
            alertType: event.type,
            pendingCount: this.pending.length - 1,
          });
          this.removePending(event.id);
          observer.next();
          observer.complete();
        },
        skip: () => {
          this.datadogService.logInfo(`[AlertQueue] Alert skipped: ${event.id}`, {
            alertId: event.id,
            alertType: event.type,
            pendingCount: this.pending.length - 1,
          });
          this.removePending(event.id);
          observer.next();
          observer.complete();
        },
      };

      this.datadogService.logInfo(`[AlertQueue] Alert started processing: ${event.id}`, {
        alertId: event.id,
        alertType: event.type,
        priority: event.priority,
        pendingCount: this.pending.length,
      });
      this.playbackSubject$.next({ event, control });

      // 🧹 Cleanup se o observable for cancelado
      return () => {
        this.removePending(event.id);
      };
    }).pipe(
      // 🛑 Cancela se receber sinal de stop
      takeUntil(this.stopCurrentCtrl$)
    );
  }

  /**
   * ⏭️ Cancela o alerta que está sendo processado atualmente
   */
  public cancelCurrent(): void {
    this.datadogService.logInfo('[AlertQueue] Current alert cancelled', {
      pendingCount: this.pending.length,
      wasPaused: this.paused,
    });
    this.stopCurrentCtrl$.next();
    // Pequeno cooldown para evitar processar o próximo antes do áudio parar
    if (!this.paused) {
      this.paused = true;
      clearTimeout(this.skipCooldownTimer);
      this.skipCooldownTimer = setTimeout(() => {
        this.resume();
      }, this.skipCooldownMs);
    }
  }

  /**
   * ⏸️ Pausa o processamento (novos alertas ficam armazenados).
   */
  public pause(): void {
    this.paused = true;
    // Invalida geração atual para bloquear itens já emitidos e re-enfileirar para retomada
    this.generation++;
    // Cancela o processamento atual para não avançar para o próximo
    this.stopCurrentCtrl$.next();
    this.datadogService.trackAction("queue_paused", {
      pending: this.pending.length,
      buffered: this.pausedBuffer.length,
      pendingIds: this.pending.map((e) => e.id),
      bufferedIds: this.pausedBuffer.map((e) => e.id),
    });
  }

  /**
   * ▶️ Retoma processamento e libera o buffer em ordem.
   */
  public resume(): void {
    if (!this.paused) return;
    this.paused = false;
    const toRelease = [...this.pausedBuffer];
    this.pausedBuffer = [];
    this.datadogService.trackAction("queue_resumed", {
      released: toRelease.length,
      pending: this.pending.length,
      releasedIds: toRelease.map((e) => e.id),
      pendingIds: this.pending.map((e) => e.id),
    });
    toRelease.forEach((evt) => this.eventQueue$.next(evt));
  }

  /**
   * 🔢 Gera ID único para o evento
   */
  private generateId(): string {
    return `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private removePending(eventId: string): void {
    this.pending = this.pending.filter((e) => e.id !== eventId);
  }

  /**
   * 🧹 Limpa fila e cancela o alerta atual (usado para comandos de clear/stop)
   */
  public clearAll(
    reason = "external_clear",
    meta: Record<string, any> = {},
    options?: { resume?: boolean }
  ): void {
    const discardedPending = this.pending.length;
    const discardedBuffered = this.pausedBuffer.length;
    // Invalida eventos em progresso/pendentes anteriores
    this.generation++;

    // Cancela o alerta atual
    this.stopCurrentCtrl$.next();

    // Esvazia as filas
    this.pending = [];
    this.pausedBuffer = [];

    // Observabilidade centralizada
    this.datadogService.trackAction("queue_cleared", {
      reason,
      discardedPending,
      discardedBuffered,
      ...meta,
    });

    // Opcionalmente reativa a fila (evita ficar "pausado" após clear)
    if (options?.resume) {
      const wasPaused = this.paused;
      this.paused = false;
      if (wasPaused) {
        // se havia itens no buffer, já foi limpo acima; apenas sinaliza retomada
        this.datadogService.trackAction("queue_resumed_after_clear", {
          ...meta,
        });
      }
      // Garante reativação do pipeline para novos eventos
      this.resume();
    }

  }

  /**
   * ✅ Verifica se o evento pertence à geração atual (não foi invalidado por um clear)
   */
  public isCurrentGeneration(event: Pick<AlertEvent, "generation">): boolean {
    return event.generation === this.generation;
  }
}
